import React, { useState, useEffect } from 'react';
import './App.css';
import ProductDesigner from './components/ProductDesigner';
import ProductList from './components/ProductList';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Notification from './components/Notification';
import Appearance from './components/AppearanceDesigner';
import CreatorInfo from './components/CreatorInfo';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './components/auth/AuthProvider';
import AdminDashboard from './pages/AdminDashboard';
import BecomeCreatorPage from './pages/creator/BecomeCreatorPage';
import DesignSystemPreview from './pages/DesignSystemPreview';
import MessagesPage from './pages/MessagesPage';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layout avec sidebar pour les pages principales (réservé aux creators)
const MainLayout = ({ children, isMobile, sidebarOpen, toggleSidebar }) => (
  <div className="app">
    <Notification />
    <Sidebar
      isOpen={sidebarOpen}
      isMobile={isMobile}
      toggleSidebar={toggleSidebar}
    />

    <div className={`main-content ${sidebarOpen && !isMobile ? 'sidebar-open' : ''}`}>
      <Header
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
      />
      
      <main className="main-area">
        {children}
      </main>
    </div>
  </div>
);

const MOBILE_MEDIA = '(max-width: 768px)';

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(MOBILE_MEDIA).matches;

function App() {
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const media = window.matchMedia(MOBILE_MEDIA);
    const syncLayout = () => {
      const mobile = media.matches;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };

    syncLayout();
    media.addEventListener('change', syncLayout);
    return () => media.removeEventListener('change', syncLayout);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen((open) => !open);
  };

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Routes standalone (sans layout) */}
          <Route path="/design-system" element={<DesignSystemPreview />} />
          
          {/* Page "Devenir créateur" - nécessite juste d'être connecté (pas creator) */}
          <Route path="/become-creator" element={
            <ProtectedRoute>
              <BecomeCreatorPage />
            </ProtectedRoute>
          } />
          
          {/* Toutes les autres routes nécessitent d'être creator */}
          <Route path="/*" element={
            <ProtectedRoute requiredRole="creator">
              <MainLayout 
                isMobile={isMobile} 
                sidebarOpen={sidebarOpen} 
                toggleSidebar={toggleSidebar}
              >
                <Routes>
                  <Route path="/" element={<Navigate to="/products" replace />} />
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/products" element={<ProductList />} />
                  <Route path="/products/new" element={<ProductDesigner />} />
                  <Route path="/products/:id" element={<ProductDesigner />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/dashboard" element={
                    <div className="page-card">
                      <h2>Tableau de bord</h2>
                      <p>Bienvenue dans votre tableau de bord d'administration.</p>
                    </div>
                  } />
                  <Route path="/settings" element={
                    <div className="page-card">
                      <h2>Paramètres</h2>
                      <p>Configurez les paramètres de votre application ici.</p>
                    </div>
                  } />
                  <Route path="/appearance" element={<Appearance />} />
                  <Route path="/presentation" element={<Navigate to="/appearance" replace />} />
                  <Route path="/creator-info" element={<CreatorInfo />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
